import { AlertColor, SnackbarOrigin } from '@mui/material';
import { createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit';
import { Office } from '../modules/Office/types/response/Office';
import { AppThunk } from './store';
import { OfficeRepository } from '../modules/Office/repository/office';
import secureLocalStorage from 'react-secure-storage';
import { ReactNode } from 'react';

export interface ISnackbar {
	snackbarActive?: boolean;
	snackbarMessage?: string;
	snackbarPosition?: SnackbarOrigin;
	severity?: AlertColor;
	variant?: 'standard' | 'filled' | 'outlined';
	children?: ReactNode;
}

export interface ISuccessComponent {
	successActive?: boolean;
	successMessage?: string;
}

export interface IPermDialog {
	permDialogOpen: boolean;
	permission?: string;
	action?: string;
	callback?: () => void;
}

export interface AppSlice {
	snackbar: ISnackbar | null;
	success: ISuccessComponent | null;
	office?: Office;
	offices?: Office[];
	officeLoading?: boolean;
	officeError?: string | null;
	permDialog?: IPermDialog;
}

const initialState: AppSlice = {
	snackbar: {},
	success: {},
	office: JSON.parse(secureLocalStorage.getItem('office') as string) as Office,
	offices: [],
	officeLoading: false,
	officeError: null,
	permDialog: {
		permDialogOpen: false,
		permission: undefined,
		action: undefined,
		callback: () => {},
	},
};

const appSlice = createSlice({
	name: 'app',
	initialState,
	reducers: {
		updateSnackbar: (state, action: PayloadAction<ISnackbar>) => {
			state.snackbar = action.payload;
		},
		updateSucess: (state, action: PayloadAction<ISuccessComponent>) => {
			state.success = action.payload;
		},
		showSucess: (state, action: PayloadAction<AppSlice>) => {
			state.success = action.payload.success;
			state.snackbar = action.payload.snackbar;
		},
		setOffice: (state, action: PayloadAction<Office>) => {
			state.office = action.payload;
			secureLocalStorage.setItem('office', JSON.stringify(action.payload));
		},
		setOffices: (state, action: PayloadAction<Office[]>) => {
			state.offices = action.payload;
		},
		setOfficeLoading: (state, action: PayloadAction<boolean>) => {
			state.officeLoading = action.payload;
		},
		setOfficeError: (state, action: PayloadAction<string | null>) => {
			state.officeError = action.payload;
		},
		openPermDialog: (
			state,
			action: PayloadAction<{ permission: string; action: string; callback?: () => void }>,
		) => {
			state.permDialog = {
				permDialogOpen: true,
				permission: action.payload.permission,
				action: action.payload.action,
				callback: action.payload.callback,
			};
		},
		closePermDialog: state => {
			const callback = state.permDialog?.callback;
			state.permDialog = { ...state.permDialog, permDialogOpen: false, callback: undefined };
			if (callback && typeof callback === 'function') {
				setTimeout(() => callback(), 0);
			}
		},
	},
});

export const {
	updateSnackbar,
	setOffice,
	setOffices,
	showSucess,
	updateSucess,
	openPermDialog,
	closePermDialog,
	setOfficeError,
	setOfficeLoading,
} = appSlice.actions;

/**
 * Fetch list of offices and update global state.
 * Sets loading/error indicators for UI feedback.
 */
export const fetchOffices = (): AppThunk => async (dispatch: Dispatch) => {
	try {
		dispatch(setOfficeLoading(true));
		dispatch(setOfficeError(null));

		const response = await OfficeRepository.listOffices();
		dispatch(setOffices(response));

		if (response.length > 0) {
			dispatch(setOffice(response[0]));
		}
	} catch (err: any) {
		const msg = typeof err === 'string' ? err : (err?.message ?? 'Failed to fetch offices');
		dispatch(setOfficeError(msg));
	} finally {
		dispatch(setOfficeLoading(false));
	}
};

export default appSlice.reducer;
